/**
 * urlHash.ts — T-237 deep-link hash encode/decode for the OptiYield SPA (pure, no DOM).
 *
 * The app has no router (single view, Ricardo's ruling), so shareable / restore-on-reload
 * state rides in the URL hash. Format follows MapLibre's `#zoom/lat/lng` viewport convention
 * and appends the app's selection state as &-params:
 *
 *   #<zoom>/<lat>/<lng>&tract=<geoid>&pin=<lat>,<lng>
 *
 * `tract` is the selected census tract; `pin` is the site-score pin (present only when a site
 * is score-pinned). Both are optional. Coordinates are rounded to a stable precision (zoom 2dp,
 * lat/lng 5dp ≈ 1.1 m) with trailing zeros trimmed, so the string is canonical and
 * encode∘decode is idempotent — the round-trip a reload/Back relies on. Parsing is defensive:
 * a malformed hash yields `null` (fall back to the default camera) rather than throwing on boot.
 */

/** Camera + selection to write into the hash. tract/pin may be omitted or null. */
export interface MapHashState {
  zoom: number
  lat: number
  lng: number
  tract?: string | null
  pin?: { lat: number; lng: number } | null
}

/** Parsed hash — tract/pin are always present as value-or-null for a stable shape. */
export interface DecodedMapHash {
  zoom: number
  lat: number
  lng: number
  tract: string | null
  pin: { lat: number; lng: number } | null
}

const ZOOM_DP = 2
const COORD_DP = 5

/** Round to `dp` decimals and drop trailing zeros, so 38.500000 → "38.5" and 4 → "4". */
function fmt(n: number, dp: number): string {
  return Number(n.toFixed(dp)).toString()
}

/** Serialise camera + selection to the URL hash (leading '#' included). */
export function encodeMapHash(state: MapHashState): string {
  let hash = `#${fmt(state.zoom, ZOOM_DP)}/${fmt(state.lat, COORD_DP)}/${fmt(state.lng, COORD_DP)}`
  if (state.tract) {
    hash += `&tract=${encodeURIComponent(state.tract)}`
  }
  if (state.pin) {
    hash += `&pin=${fmt(state.pin.lat, COORD_DP)},${fmt(state.pin.lng, COORD_DP)}`
  }
  return hash
}

function inRange(n: number, min: number, max: number): boolean {
  return Number.isFinite(n) && n >= min && n <= max
}

/** Parse a valid lat/lng pair from "lat,lng"; null if malformed or out of range. */
function parsePin(value: string): { lat: number; lng: number } | null {
  const parts = value.split(',')
  if (parts.length !== 2) return null
  const lat = Number(parts[0])
  const lng = Number(parts[1])
  if (!inRange(lat, -90, 90) || !inRange(lng, -180, 180)) return null
  return { lat, lng }
}

/**
 * Parse a URL hash back into structured state, or null if it isn't a valid viewport hash.
 * Accepts the string with or without the leading '#'. Out-of-range coordinates are rejected
 * (a garbage hash must not restore an impossible camera); a malformed pin is dropped while the
 * viewport + tract are still returned.
 */
export function parseMapHash(hash: string): DecodedMapHash | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  if (raw === '') return null

  const segments = raw.split('&')
  const triplet = (segments[0] ?? '').split('/')
  if (triplet.length < 3) return null

  const zoom = Number(triplet[0])
  const lat = Number(triplet[1])
  const lng = Number(triplet[2])
  if (!inRange(zoom, 0, 24) || !inRange(lat, -90, 90) || !inRange(lng, -180, 180)) return null

  let tract: string | null = null
  let pin: { lat: number; lng: number } | null = null
  for (const seg of segments.slice(1)) {
    const eq = seg.indexOf('=')
    if (eq < 0) continue
    const key = seg.slice(0, eq)
    const value = seg.slice(eq + 1)
    if (key === 'tract') {
      const decoded = decodeURIComponent(value)
      tract = decoded !== '' ? decoded : null
    } else if (key === 'pin') {
      pin = parsePin(value)
    }
  }

  return { zoom, lat, lng, tract, pin }
}
